import React from 'react';
import cs from 'classnames';
import { Link } from 'react-router-dom';
import { Form, useField } from 'formik';
import { App_PortalFragment, Signature_ApplicationFragment, useRequestSmsCodeMutation } from '../../generated.graphql';
import { TextFieldGroup } from 'shared/formik/TextFieldGroup';
import { slugs, useRentalSteps } from '../../app/steps';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { formatPhoneNumber } from 'shared/utils/phone';

interface Props {
  portal: App_PortalFragment;
  application: Signature_ApplicationFragment;
  canSubmit: boolean;
  onSubmit: () => void;
  onFallback: () => void;
}

interface SignatureInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
}

const SignatureInput = (props: SignatureInputProps) => {
  const [/* field */, /* meta */, helpers] = useField(props);
  return (
    <TextFieldGroup
      autoFocus={true}
      name={props.name}
      onChange={e => {
        helpers.setValue(e.target.value.trim().toLocaleUpperCase());
      }}
    />
  );
};

export const SmsSignature = ({ portal, application, canSubmit, onSubmit, onFallback }: Props) => {
  const steps = useRentalSteps({slug: slugs.signature});
  const aboutSteps = useRentalSteps({slug: slugs.aboutMe});
  const [resultRequestSms, executeRequestSms] = useRequestSmsCodeMutation();
  const requestErrors = resultRequestSms.data?.sendSmsSignature?.errors?.map(field => ({
    label: field?.field,
    message: field?.messages.map(e => <>{e}<br/></>)
  }));

  const hasRequested = application.remainingSmsSignatureCount < 3;
  const phoneNumber = formatPhoneNumber(application.customer.phoneMobile).replaceAll(' ', '\u00A0');

  // only show manual signature option if we're out of sms quota, or there was a graphql error
  // (errors from the mutation don't trigger manual signature)
  const showManualSignature = application.remainingSmsSignatureCount === 0 || resultRequestSms.error !== undefined;
  const handleRequestSmsCode = async () => {
    await executeRequestSms({ input: { portalSlug: portal.slug, number: application.customer.phoneMobile }});
  }
  
  if (!application.digitalSignatureRequired) {
    return null;
  }

  return (
    <>
      <p className="my-5">
        <em>
          To make sure we have the correct details and for added security, request an SMS code below.
          We will send a text to <b>{phoneNumber}</b> to act as your digital signature.
          {aboutSteps.this?.link && (<> If this number is incorrect, please <Link to={aboutSteps.this.link}>update it on your application</Link>.</>)}
        </em>
      </p>
      
      <Form noValidate>
        <div className="my-4">
          <div className="form-row">
            <div className={cs("col-12 col-sm-auto col-md-auto text-center", hasRequested ? "order-3 order-md-1" : "order-1")}>
              <button
                type="button"
                className="btn btn-secondary btn-wide mt-1"
                onClick={handleRequestSmsCode}
                disabled={resultRequestSms.fetching || application.remainingSmsSignatureCount === 0}
              >
                {hasRequested ? 'Resend SMS Code' : 'Get SMS Code'} {resultRequestSms.fetching && (<FontAwesomeIcon icon={faSpinnerThird} spin />)}
              </button>
              {hasRequested && <div className="text-muted small p-2">{application.remainingSmsSignatureCount} attempt{application.remainingSmsSignatureCount === 1 ? '' : 's'} remaining</div>}
            </div>
            <div className="col-12 col-sm-6 col-md-4 order-1 order-md-2">
              {hasRequested && (
                <SignatureInput name="signature" />
              )}
            </div>
            <div className="col-12 col-sm-auto col-md-auto order-2 order-md-3 order-md-1 text-center">
              {hasRequested && (
                <button
                  type="submit"
                  className="btn btn-primary btn-wide mt-1"
                  onClick={() => {
                    onSubmit();
                  }}
                  disabled={!canSubmit}
                >
                  Submit Application
                </button>
              )}
            </div>
            <div className="col-12">
              {requestErrors && (
                <div className="text-danger p-2">
                  <div>There was an issue when requesting an SMS:</div>
                  <div className="small">
                    {requestErrors.map((error, i) => 
                      <div key={i}>{error.label && <b className="mr-2">{error.label}:</b> }{error.message}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* <div className="col-12">
              <label htmlFor="digitalSignatureConfirmation">Please enter the code here as your digital signature</label>
            </div>
            <div className="col-md-6">
              <SignatureInput name="signature" signature={application.digitalSignature.trim().toLocaleUpperCase()} />
            </div> */}
          </div>
          <div className="d-flex flex-column flex-sm-row my-4">
            {/*aboutSteps?.this?.link && <Link className="mr-4" to={aboutSteps.this.link}>Change phone number on application</Link>*/}
            {showManualSignature && (<button type="button" className="btn btn-link p-0 font-weight-light order-sm-2" onClick={() => onFallback()}>Enter digital signature manually</button>)}
            {steps?.prev?.link && <Link className="btn btn-link p-0 font-weight-light mr-sm-4 order-sm-1" to={steps.prev.link}>Back to application summary</Link>}
          </div>
        </div>
      </Form>
    </>
  );
}